// hooks/useExpansionState.ts

import { useState, useCallback, useEffect } from 'react';
import {
  EXPANSION_KEY_SEPARATOR,
  EXPANSION_KEY_MAJOR_PREFIX,
  EXPANSION_KEY_REQ_PREFIX
} from '../utils/constants';

// =============================================================================
// TYPES
// =============================================================================

/**
 * Parsed path components
 */
export interface ParsedPath {
  parts: string[];
  isMajor: boolean;
  isRequirement: boolean;
  majorIndex: number | null;
  requirementIndex: number | null;
}

/**
 * Expansion key helper functions
 */
export interface ExpansionKeyHelpers {
  majorPath: (majorIndex: number) => string;
  requirementPath: (majorIndex: number, reqIndex: number) => string;
  nestedPath: (basePath: string, childIndex: number) => string;
  courseKey: (requirementId: string, courseIndex: number) => string;
  parsePath: (path: string) => ParsedPath;
}

/**
 * Hierarchical data structure for requirements
 */
export interface HierarchicalRequirement {
  children?: HierarchicalRequirement[];
  [key: string]: unknown;
}

/**
 * Program data structure (major or minor)
 */
export interface HierarchicalProgram {
  requirements?: HierarchicalRequirement[];
  [key: string]: unknown;
}

/**
 * Hierarchical data structure passed to the hook
 */
export interface HierarchicalData {
  majors?: HierarchicalProgram[];
  minors?: HierarchicalProgram[];
  citations?: HierarchicalProgram[];
}

/**
 * Expansion statistics
 */
export interface ExpansionStats {
  totalNodes: number;
  expandedNodesCount: number;
  totalCourses: number;
  expandedCoursesCount: number;
  allNodesExpanded: boolean;
  noNodesExpanded: boolean;
}

/**
 * State for tracking expanded nodes
 */
export interface ExpandedNodesState {
  [nodeId: string]: boolean;
}

/**
 * State for tracking expanded courses
 */
export interface ExpandedCoursesState {
  [key: string]: boolean;
}

/**
 * Return type for the useExpansionState hook
 */
export interface UseExpansionStateReturn {
  // State
  expandedNodes: ExpandedNodesState;
  expandedCourses: ExpandedCoursesState;

  // Node operations
  toggleNode: (nodeId: string) => void;
  expandNode: (nodeId: string) => void;
  collapseNode: (nodeId: string) => void;
  isNodeExpanded: (nodeId: string) => boolean;

  // Course operations
  toggleCourse: (requirementId: string, courseIndex: number) => void;
  isCourseExpanded: (requirementId: string, courseIndex: number) => boolean;

  // Bulk operations
  expandAll: () => void;
  collapseAll: () => void;
  bulkToggleNodes: (nodeIds: string[], forceState?: boolean | null) => void;
  toggleProgramWithChildren: (programPath: string, requirements?: HierarchicalRequirement[]) => void;
  resetExpansionState: () => void;

  // Getters
  getExpandedNodeIds: () => string[];
  getExpandedCourseKeys: () => string[];

  // Statistics
  expansionStats: ExpansionStats;

  // Utilities
  keyHelpers: ExpansionKeyHelpers;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Utility functions for generating expansion keys
 */
export const expansionKeyHelpers: ExpansionKeyHelpers = {
  /**
   * Generate a major path key
   */
  majorPath: (majorIndex: number): string =>
    `${EXPANSION_KEY_MAJOR_PREFIX}${EXPANSION_KEY_SEPARATOR}${majorIndex}`,

  /**
   * Generate a requirement path key
   */
  requirementPath: (majorIndex: number, reqIndex: number): string =>
    `${EXPANSION_KEY_MAJOR_PREFIX}${EXPANSION_KEY_SEPARATOR}${majorIndex}${EXPANSION_KEY_SEPARATOR}${EXPANSION_KEY_REQ_PREFIX}${EXPANSION_KEY_SEPARATOR}${reqIndex}`,

  /**
   * Generate a nested requirement path key
   */
  nestedPath: (basePath: string, childIndex: number): string =>
    `${basePath}${EXPANSION_KEY_SEPARATOR}${childIndex}`,

  /**
   * Generate a course expansion key
   */
  courseKey: (requirementId: string, courseIndex: number): string =>
    `${requirementId}${EXPANSION_KEY_SEPARATOR}${courseIndex}`,

  /**
   * Parse a path to extract components
   */
  parsePath: (path: string): ParsedPath => {
    const parts = path.split(EXPANSION_KEY_SEPARATOR);
    const majorPrefixIndex = parts.indexOf(EXPANSION_KEY_MAJOR_PREFIX);
    const reqPrefixIndex = parts.indexOf(EXPANSION_KEY_REQ_PREFIX);

    return {
      parts,
      isMajor: parts.includes(EXPANSION_KEY_MAJOR_PREFIX),
      isRequirement: parts.includes(EXPANSION_KEY_REQ_PREFIX),
      majorIndex: majorPrefixIndex !== -1 && parts[majorPrefixIndex + 1] ?
        parseInt(parts[majorPrefixIndex + 1]) : null,
      requirementIndex: reqPrefixIndex !== -1 && parts[reqPrefixIndex + 1] ?
        parseInt(parts[reqPrefixIndex + 1]) : null
    };
  }
};

// =============================================================================
// HOOK
// =============================================================================

/**
 * Custom hook for managing expansion state across the degree audit hierarchy
 * Handles both node expansion (requirements) and course expansion (course details)
 */
export const useExpansionState = (
  hierarchicalData: HierarchicalData | null | undefined,
  autoExpand = true
): UseExpansionStateReturn => {
  // State for expanded nodes (requirements and categories)
  const [expandedNodes, setExpandedNodes] = useState<ExpandedNodesState>({});

  // State for expanded courses (course detail views)
  const [expandedCourses, setExpandedCourses] = useState<ExpandedCoursesState>({});

  // Toggle a node's expansion state
  const toggleNode = useCallback((nodeId: string) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeId]: !prev[nodeId]
    }));
  }, []);

  // Toggle a course's expansion state
  const toggleCourse = useCallback((requirementId: string, courseIndex: number) => {
    const key = expansionKeyHelpers.courseKey(requirementId, courseIndex);
    setExpandedCourses(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  }, []);

  // Expand a specific node
  const expandNode = useCallback((nodeId: string) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeId]: true
    }));
  }, []);

  // Collapse a specific node
  const collapseNode = useCallback((nodeId: string) => {
    setExpandedNodes(prev => ({
      ...prev,
      [nodeId]: false
    }));
  }, []);

  // Expand all nodes (majors and minors) - V2: Optimized with requestIdleCallback
  const expandAll = useCallback(() => {
    // Use requestIdleCallback to avoid blocking the main thread
    const computeAndSetExpansion = () => {
      const allNodeIds: ExpandedNodesState = {};

      // Helper to collect all requirement paths recursively
      const collectRequirementPaths = (requirements: HierarchicalRequirement[] | undefined, programPath: string) => {
        if (!requirements || requirements.length === 0) return;
        requirements.forEach((req, reqIndex) => {
          const reqPath = `${programPath}-req-${reqIndex}`;
          allNodeIds[reqPath] = true;

          // Recursively collect child paths
          const collectChildren = (children: HierarchicalRequirement[] | undefined, basePath: string) => {
            if (!children || children.length === 0) return;
            children.forEach((child, index) => {
              const childPath = `${basePath}-${index}`;
              allNodeIds[childPath] = true;
              if (child.children && child.children.length > 0) {
                collectChildren(child.children, childPath);
              }
            });
          };

          if (req.children && req.children.length > 0) {
            collectChildren(req.children, reqPath);
          }
        });
      };

      // Collect IDs from all majors
      if (hierarchicalData?.majors) {
        hierarchicalData.majors.forEach((major, majorIndex) => {
          const programPath = `major-${majorIndex}`;
          allNodeIds[programPath] = true;
          collectRequirementPaths(major.requirements, programPath);
        });
      }

      // Collect IDs from all minors
      if (hierarchicalData?.minors) {
        hierarchicalData.minors.forEach((minor, minorIndex) => {
          const programPath = `minor-${minorIndex}`;
          allNodeIds[programPath] = true;
          collectRequirementPaths(minor.requirements, programPath);
        });
      }

      // Collect IDs from all citations
      if (hierarchicalData?.citations) {
        hierarchicalData.citations.forEach((citation: any, citationIndex: number) => {
          const programPath = `citation-${citationIndex}`;
          allNodeIds[programPath] = true;
          collectRequirementPaths(citation.requirements, programPath);
        });
      }

      setExpandedNodes(allNodeIds);
    };

    // Use requestIdleCallback if available, otherwise use setTimeout
    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(computeAndSetExpansion, { timeout: 100 });
    } else {
      setTimeout(computeAndSetExpansion, 0);
    }
  }, [hierarchicalData]);

  // Collapse all nodes
  const collapseAll = useCallback(() => {
    setExpandedNodes({});
  }, []);

  // Auto-expand all nodes when data changes (if enabled)
  useEffect(() => {
    if (autoExpand && hierarchicalData?.majors && hierarchicalData.majors.length > 0) {
      expandAll();
    }
  }, [hierarchicalData, autoExpand, expandAll]);

  // Get expansion state for a specific node
  const isNodeExpanded = useCallback((nodeId: string): boolean => {
    return !!expandedNodes[nodeId];
  }, [expandedNodes]);

  // Get expansion state for a specific course
  const isCourseExpanded = useCallback((requirementId: string, courseIndex: number): boolean => {
    const key = expansionKeyHelpers.courseKey(requirementId, courseIndex);
    return !!expandedCourses[key];
  }, [expandedCourses]);

  // Get all expanded node IDs
  const getExpandedNodeIds = useCallback((): string[] => {
    return Object.keys(expandedNodes).filter(nodeId => expandedNodes[nodeId]);
  }, [expandedNodes]);

  // Get all expanded course keys
  const getExpandedCourseKeys = useCallback((): string[] => {
    return Object.keys(expandedCourses).filter(key => expandedCourses[key]);
  }, [expandedCourses]);

  // Bulk operations for nodes
  const bulkToggleNodes = useCallback((nodeIds: string[], forceState: boolean | null = null) => {
    setExpandedNodes(prev => {
      const updated = { ...prev };
      nodeIds.forEach(nodeId => {
        updated[nodeId] = forceState !== null ? forceState : !prev[nodeId];
      });
      return updated;
    });
  }, []);

  // Toggle a program and all its children (for hierarchical expand/collapse)
  const toggleProgramWithChildren = useCallback((programPath: string, requirements?: HierarchicalRequirement[]) => {
    setExpandedNodes(prev => {
      const isProgramExpanded = prev[programPath];
      const newState = !isProgramExpanded;
      const updated: ExpandedNodesState = { ...prev, [programPath]: newState };

      // Helper to collect all child paths recursively
      const collectChildPaths = (children: HierarchicalRequirement[] | undefined, basePath: string) => {
        if (!children || children.length === 0) return;
        children.forEach((child, index) => {
          const childPath = `${basePath}-${index}`;
          updated[childPath] = newState;
          if (child.children && child.children.length > 0) {
            collectChildPaths(child.children, childPath);
          }
        });
      };

      // Collect all requirement paths under this program
      if (requirements && requirements.length > 0) {
        requirements.forEach((req, reqIndex) => {
          const reqPath = `${programPath}-req-${reqIndex}`;
          updated[reqPath] = newState;
          if (req.children && req.children.length > 0) {
            collectChildPaths(req.children, reqPath);
          }
        });
      }

      return updated;
    });
  }, []);

  // Clear all expansion state
  const resetExpansionState = useCallback(() => {
    setExpandedNodes({});
    setExpandedCourses({});
  }, []);

  // Calculate expansion statistics
  const expansionStats: ExpansionStats = {
    totalNodes: Object.keys(expandedNodes).length,
    expandedNodesCount: getExpandedNodeIds().length,
    totalCourses: Object.keys(expandedCourses).length,
    expandedCoursesCount: getExpandedCourseKeys().length,
    allNodesExpanded: Object.keys(expandedNodes).length > 0 &&
      Object.values(expandedNodes).every(expanded => expanded),
    noNodesExpanded: Object.values(expandedNodes).every(expanded => !expanded)
  };

  return {
    // State
    expandedNodes,
    expandedCourses,

    // Node operations
    toggleNode,
    expandNode,
    collapseNode,
    isNodeExpanded,

    // Course operations
    toggleCourse,
    isCourseExpanded,

    // Bulk operations
    expandAll,
    collapseAll,
    bulkToggleNodes,
    toggleProgramWithChildren,
    resetExpansionState,

    // Getters
    getExpandedNodeIds,
    getExpandedCourseKeys,

    // Statistics
    expansionStats,

    // Utilities
    keyHelpers: expansionKeyHelpers
  };
};
